import axios from 'axios';
import config from '../config';
import * as XLSX from 'xlsx';

const EXPORT_VERSION = '1.0';

/**
 * Build the export payload from current Redux data.
 * Uses CategoryName instead of CategoryId for cross-account portability.
 */
export function buildExportData(products, categories) {
  const categoryMap = {};
  categories.forEach(c => { categoryMap[c.CategoryId] = c.Name; });

  const exportCategories = categories.map(c => ({
    Name: c.Name,
    PrinterId: c.PrinterId,
    Picture: c.Picture || null,
    InternalOnly: !!c.InternalOnly,
  }));

  const exportProducts = products.map(p => ({
    Name: p.Name,
    CategoryName: categoryMap[p.CategoryId] || '',
    Description: p.Description || '',
    SKU: p.SKU || '',
    Barcode: p.Barcode || '',
    Price: p.Price,
    FeeOption: p.FeeOption,
    TaxRate: p.TaxRate,
    Picture: p.Picture || null,
    Inventory: p.Inventory || 0,
    InternalOnly: !!p.InternalOnly,
    Variations: (p.Variations || []).map(v => ({
      Name: v.Name,
      Cost: v.Cost,
      Inventory: v.Inventory || 0,
      SKU: v.SKU || '',
    })),
    Options: (p.Options || []).map(o => ({
      Name: o.Name,
      Cost: o.Cost,
      Inventory: o.Inventory || 0,
      TrackInventory: !!o.TrackInventory,
      SKU: o.SKU || '',
    })),
  }));

  return {
    version: EXPORT_VERSION,
    exportDate: new Date().toISOString(),
    categories: exportCategories,
    products: exportProducts,
  };
}

/**
 * Trigger a JSON file download in the browser.
 */
export function downloadJSON(data, filename) {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Validate import data structure. Returns { valid, errors }.
 */
export function validateImportData(data) {
  const errors = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Invalid JSON structure'] };
  }
  if (!data.version) {
    errors.push('Missing export version');
  }
  if (!Array.isArray(data.categories)) {
    errors.push('Missing or invalid categories array');
  }
  if (!Array.isArray(data.products)) {
    errors.push('Missing or invalid products array');
  }

  if (errors.length) return { valid: false, errors };

  data.categories.forEach((c, i) => {
    if (!c.Name || typeof c.Name !== 'string') {
      errors.push(`Category ${i + 1}: missing Name`);
    }
  });

  data.products.forEach((p, i) => {
    if (!p.Name || typeof p.Name !== 'string') {
      errors.push(`Product ${i + 1}: missing Name`);
    }
    if (p.Variations && !Array.isArray(p.Variations)) {
      errors.push(`Product ${i + 1} (${p.Name}): Variations must be an array`);
    }
    if (p.Options && !Array.isArray(p.Options)) {
      errors.push(`Product ${i + 1} (${p.Name}): Options must be an array`);
    }
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Attempt to download an image from a URL and return it as a File object.
 * Returns null if the image cannot be fetched.
 */
async function fetchImageAsFile(imageUrl, filename) {
  try {
    if (!imageUrl) return null;
    const response = await fetch(imageUrl, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const ext = imageUrl.split('.').pop().split('?')[0] || 'jpg';
    return new File([blob], `${filename}.${ext}`, { type: blob.type || 'image/jpeg' });
  } catch (e) {
    console.warn('Could not fetch image:', imageUrl, e.message);
    return null;
  }
}

/**
 * Upload a product image file via the media endpoint.
 */
async function uploadProductImage(picFile) {
  const formData = new FormData();
  formData.append('file', picFile);
  const resp = await axios.post('/media/uploadproductpicture', formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
  return resp.data.pictureFile;
}

/**
 * Upload a category image file via the media endpoint.
 */
async function uploadCategoryImage(picFile) {
  const formData = new FormData();
  formData.append('file', picFile);
  const resp = await axios.post('/media/uploadproductcategorypicture', formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });
  return resp.data.pictureFile;
}

/**
 * Import product data into the current merchant account.
 *
 * @param {object} importData - Parsed JSON from the export file
 * @param {number} merchantId - Current merchant ID
 * @param {object} existingCategories - Current categories in the account
 * @param {function} onProgress - Callback with { step, total, message }
 * @returns {{ categoriesCreated, productsCreated, variationsCreated, optionsCreated, imagesFailed, errors }}
 */
export async function importProductData(importData, merchantId, existingCategories, onProgress) {
  const result = {
    categoriesCreated: 0,
    categoriesSkipped: 0,
    productsCreated: 0,
    variationsCreated: 0,
    optionsCreated: 0,
    imagesFailed: 0,
    errors: [],
  };

  const totalSteps = importData.categories.length + importData.products.length;
  let currentStep = 0;

  // Build a map of existing category names to IDs
  const categoryNameToId = {};
  existingCategories.forEach(c => {
    categoryNameToId[c.Name.toLowerCase()] = c.CategoryId;
  });

  // --- Phase 1: Create categories ---
  onProgress({ step: 0, total: totalSteps, message: 'Creating categories...' });

  for (const cat of importData.categories) {
    currentStep++;
    const existingId = categoryNameToId[cat.Name.toLowerCase()];

    if (existingId) {
      // Category already exists, skip creation
      result.categoriesSkipped++;
      onProgress({ step: currentStep, total: totalSteps, message: `Category "${cat.Name}" already exists, skipping` });
      continue;
    }

    try {
      // Try to fetch and upload the category image
      let picFileName = null;
      if (cat.Picture) {
        const imgFile = await fetchImageAsFile(cat.Picture, `cat_${cat.Name}`);
        if (imgFile) {
          picFileName = await uploadCategoryImage(imgFile);
        } else {
          result.imagesFailed++;
        }
      }

      const categoryPayload = {
        CategoryId: 0,
        Name: cat.Name,
        PrinterId: cat.PrinterId || 1,
        Picture: picFileName,
        MerchantId: merchantId,
        InternalOnly: !!cat.InternalOnly,
      };

      const resp = await axios.post('/api/product/category/add', categoryPayload);
      if (resp.data.status === 'Error') {
        result.errors.push(`Category "${cat.Name}": ${resp.data.message}`);
      } else {
        const newCat = resp.data.category;
        categoryNameToId[cat.Name.toLowerCase()] = newCat.CategoryId;
        result.categoriesCreated++;
      }
    } catch (err) {
      result.errors.push(`Category "${cat.Name}": ${err.message}`);
    }

    onProgress({ step: currentStep, total: totalSteps, message: `Category "${cat.Name}" created` });
  }

  // --- Phase 2: Create products ---
  onProgress({ step: currentStep, total: totalSteps, message: 'Creating products...' });

  for (const prod of importData.products) {
    currentStep++;
    onProgress({ step: currentStep, total: totalSteps, message: `Importing "${prod.Name}"...` });

    try {
      // Resolve category
      const categoryId = categoryNameToId[(prod.CategoryName || '').toLowerCase()] || 0;

      // Try to fetch and upload the product image
      let picFileName = null;
      if (prod.Picture) {
        const imgFile = await fetchImageAsFile(prod.Picture, `prod_${prod.Name}`);
        if (imgFile) {
          picFileName = await uploadProductImage(imgFile);
        } else {
          result.imagesFailed++;
        }
      }

      const productPayload = {
        ProductId: 0,
        Name: prod.Name,
        CategoryId: categoryId,
        Description: prod.Description || '',
        SKU: prod.SKU || '',
        Barcode: prod.Barcode || '',
        Price: prod.Price || 0,
        FeeOption: prod.FeeOption || 1,
        TaxRate: prod.TaxRate || 0,
        Picture: picFileName,
        Inventory: prod.Inventory || 0,
        InternalOnly: !!prod.InternalOnly,
        MerchantId: merchantId,
        Variations: [],
        Options: [],
      };

      const resp = await axios.post('/api/product/add', productPayload);
      if (resp.data.status === 'Error') {
        result.errors.push(`Product "${prod.Name}": ${resp.data.message}`);
        continue;
      }

      const newProductId = resp.data.product.ProductId;
      result.productsCreated++;

      // Create variations
      if (prod.Variations && prod.Variations.length > 0) {
        for (const v of prod.Variations) {
          try {
            const vResp = await axios.post(`/api/product/add/variation/${newProductId}`, {
              VariationId: 0,
              Name: v.Name,
              Cost: v.Cost || 0,
              Inventory: v.Inventory == null ? null : Number(v.Inventory) || 0,
              SKU: v.SKU || '',
              // Import already includes the full variation list; do not auto-seed original.
              SeedOriginal: false,
            });
            if (vResp.data.status === 'Error') {
              result.errors.push(`Variation "${v.Name}" for "${prod.Name}": ${vResp.data.message}`);
            } else {
              result.variationsCreated++;
            }
          } catch (err) {
            result.errors.push(`Variation "${v.Name}" for "${prod.Name}": ${err.message}`);
          }
        }
      }

      // Create options
      if (prod.Options && prod.Options.length > 0) {
        for (const o of prod.Options) {
          try {
            const oResp = await axios.post(`/api/product/add/option/${newProductId}`, {
              OptionId: 0,
              Name: o.Name,
              Cost: o.Cost || 0,
              Inventory: o.Inventory || 0,
              TrackInventory: !!o.TrackInventory,
              SKU: o.SKU || '',
            });
            if (oResp.data.status === 'Error') {
              result.errors.push(`Option "${o.Name}" for "${prod.Name}": ${oResp.data.message}`);
            } else {
              result.optionsCreated++;
            }
          } catch (err) {
            result.errors.push(`Option "${o.Name}" for "${prod.Name}": ${err.message}`);
          }
        }
      }
    } catch (err) {
      result.errors.push(`Product "${prod.Name}": ${err.message}`);
    }

    onProgress({ step: currentStep, total: totalSteps, message: `"${prod.Name}" imported` });
  }

  return result;
}

// ─── Excel Export / Import ───────────────────────────────────────────

/**
 * Export products & categories to an Excel (.xlsx) file with two sheets.
 */
const CATEGORY_SHEET_ALIASES = ['categories', 'categorías', 'categorias', 'catégories', 'categories'];
const PRODUCT_SHEET_ALIASES = ['products', 'productos', 'produits'];

const HEADER_ALIASES = {
    name: 'Name',
    nombre: 'Name',
    nom: 'Name',
    printerid: 'PrinterId',
    picture: 'Picture',
    imagen: 'Picture',
    image: 'Picture',
    internalonly: 'InternalOnly',
    solointerno: 'InternalOnly',
    interneuniquement: 'InternalOnly',
    categoryname: 'CategoryName',
    nombrecategoria: 'CategoryName',
    nomcategorie: 'CategoryName',
    description: 'Description',
    descripción: 'Description',
    sku: 'SKU',
    barcode: 'Barcode',
    'código de barras': 'Barcode',
    'code-barres': 'Barcode',
    price: 'Price',
    precio: 'Price',
    prix: 'Price',
    feeoption: 'FeeOption',
    opciontarifa: 'FeeOption',
    optionfrais: 'FeeOption',
    taxrate: 'TaxRate',
    tasaimpuesto: 'TaxRate',
    tauxtaxe: 'TaxRate',
    inventory: 'Inventory',
    inventario: 'Inventory',
    inventaire: 'Inventory',
    variations: 'Variations',
    variaciones: 'Variations',
    variantes: 'Variations',
    options: 'Options',
    opciones: 'Options',
};

function findSheetName(sheetNames, aliases) {
    return sheetNames.find((n) => aliases.includes(String(n || '').trim().toLowerCase()));
}

function normalizeImportRow(row) {
    const out = {};
    Object.keys(row || {}).forEach((key) => {
        const canon = HEADER_ALIASES[String(key).trim().toLowerCase()] || key;
        out[canon] = row[key];
    });
    return out;
}

export function downloadExcel(products, categories, filename, t) {
    const label = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
  const categoryMap = {};
  categories.forEach(c => { categoryMap[c.CategoryId] = c.Name; });

  const catRows = categories.map(c => ({
    [label('excel.name', 'Name')]: c.Name,
    [label('excel.printerId', 'PrinterId')]: c.PrinterId,
    [label('excel.picture', 'Picture')]: c.Picture || '',
    [label('excel.internalOnly', 'InternalOnly')]: c.InternalOnly ? 1 : 0,
  }));

  const prodRows = products.map(p => ({
    [label('excel.name', 'Name')]: p.Name,
    [label('excel.categoryName', 'CategoryName')]: categoryMap[p.CategoryId] || '',
    [label('excel.description', 'Description')]: p.Description || '',
    [label('excel.sku', 'SKU')]: p.SKU || '',
    [label('excel.barcode', 'Barcode')]: p.Barcode || '',
    [label('excel.price', 'Price')]: p.Price,
    [label('excel.feeOption', 'FeeOption')]: p.FeeOption,
    [label('excel.taxRate', 'TaxRate')]: p.TaxRate,
    [label('excel.picture', 'Picture')]: p.Picture || '',
    [label('excel.inventory', 'Inventory')]: p.Inventory || 0,
    [label('excel.internalOnly', 'InternalOnly')]: p.InternalOnly ? 1 : 0,
    [label('excel.variations', 'Variations')]: (p.Variations || []).map(v => `${v.Name}:${v.Cost}:${v.Inventory || 0}:${v.SKU || ''}`).join('; '),
    [label('excel.options', 'Options')]: (p.Options || []).map(o => `${o.Name}:${o.Cost}:${o.Inventory || 0}:${o.SKU || ''}:${o.TrackInventory ? 1 : 0}`).join('; '),
  }));

  const wb = XLSX.utils.book_new();

  const catsSheet = XLSX.utils.json_to_sheet(catRows);
  XLSX.utils.book_append_sheet(wb, catsSheet, label('excel.sheetCategories', 'Categories'));

  const prodsSheet = XLSX.utils.json_to_sheet(prodRows);
  XLSX.utils.book_append_sheet(wb, prodsSheet, label('excel.sheetProducts', 'Products'));

  XLSX.writeFile(wb, filename);
}

/**
 * Parse an Excel file (ArrayBuffer) into the standard import data structure.
 * Returns the same shape as a JSON import: { version, exportDate, categories, products }.
 */
export function parseExcelImport(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });

  // --- Categories sheet ---
  const catSheetName = findSheetName(wb.SheetNames, CATEGORY_SHEET_ALIASES);
  if (!catSheetName) throw new Error('Missing "Categories" sheet in workbook');
  const catRows = XLSX.utils.sheet_to_json(wb.Sheets[catSheetName]).map(normalizeImportRow);

  const categories = catRows.map(r => ({
    Name: String(r.Name || ''),
    PrinterId: Number(r.PrinterId) || 1,
    Picture: r.Picture || null,
    InternalOnly: r.InternalOnly === 1 || r.InternalOnly === true || String(r.InternalOnly).toLowerCase() === 'true',
  }));

  // --- Products sheet ---
  const prodSheetName = findSheetName(wb.SheetNames, PRODUCT_SHEET_ALIASES);
  if (!prodSheetName) throw new Error('Missing "Products" sheet in workbook');
  const prodRows = XLSX.utils.sheet_to_json(wb.Sheets[prodSheetName]).map(normalizeImportRow);

  const products = prodRows.map(r => {
    const variations = parseVariationTokens(r.Variations);
    const options = parseOptionTokens(r.Options);

    return {
      Name: String(r.Name || ''),
      CategoryName: String(r.CategoryName || ''),
      Description: String(r.Description || ''),
      SKU: String(r.SKU || ''),
      Barcode: String(r.Barcode || ''),
      Price: Number(r.Price) || 0,
      FeeOption: Number(r.FeeOption) || 1,
      TaxRate: Number(r.TaxRate) || 0,
      Picture: r.Picture || null,
      Inventory: Number(r.Inventory) || 0,
      InternalOnly: r.InternalOnly === 1 || r.InternalOnly === true || String(r.InternalOnly).toLowerCase() === 'true',
      Variations: variations,
      Options: options,
    };
  });

  return {
    version: EXPORT_VERSION,
    exportDate: new Date().toISOString(),
    categories,
    products,
  };
}

/**
 * Parse a semicolon-delimited string of variation tokens.
 * New: "Name:Cost:Inventory:SKU". Old: "Name:Cost".
 */
function parseVariationTokens(str) {
  if (!str || typeof str !== 'string') return [];
  return str.split(';')
    .map(s => s.trim())
    .filter(Boolean)
    .map(pair => {
      const parts = pair.split(':').map(p => p.trim());
      if (parts.length >= 4) {
        return {
          Name: parts.slice(0, parts.length - 3).join(':'),
          Cost: Number(parts[parts.length - 3]) || 0,
          Inventory: Number(parts[parts.length - 2]) || 0,
          SKU: parts[parts.length - 1] || '',
        };
      }
      if (parts.length === 2) {
        return { Name: parts[0], Cost: Number(parts[1]) || 0 };
      }
      if (parts.length === 1) {
        return { Name: parts[0], Cost: 0 };
      }
      return { Name: parts[0], Cost: Number(parts[1]) || 0 };
    });
}

/**
 * Parse a semicolon-delimited string of option tokens.
 * New: "Name:Cost:Inventory:SKU:Track". Old: "Name:Cost".
 */
function parseOptionTokens(str) {
  if (!str || typeof str !== 'string') return [];
  return str.split(';')
    .map(s => s.trim())
    .filter(Boolean)
    .map(pair => {
      const parts = pair.split(':').map(p => p.trim());
      if (parts.length >= 5) {
        return {
          Name: parts.slice(0, parts.length - 4).join(':'),
          Cost: Number(parts[parts.length - 4]) || 0,
          Inventory: Number(parts[parts.length - 3]) || 0,
          SKU: parts[parts.length - 2] || '',
          TrackInventory: parts[parts.length - 1] === '1' || parts[parts.length - 1].toLowerCase() === 'true',
        };
      }
      if (parts.length === 2) {
        return { Name: parts[0], Cost: Number(parts[1]) || 0, TrackInventory: false };
      }
      if (parts.length === 1) {
        return { Name: parts[0], Cost: 0, TrackInventory: false };
      }
      return { Name: parts[0], Cost: Number(parts[1]) || 0, TrackInventory: false };
    });
}

/**
 * Parse a semicolon-delimited string of "Name:Cost" pairs into an array.
 * e.g. "Small:0; Large:2.50" → [{ Name: "Small", Cost: 0 }, { Name: "Large", Cost: 2.5 }]
 */
function parseDelimitedPairs(str) {
  return parseVariationTokens(str);
}
